import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Badge, Divider, Avatar } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';

const AdminSidebar = ({ activeTab, onTabChange, onLogout }) => {
    const navigate = useNavigate();

    const handleMainDashboard = () => {
        navigate('/dashboard');
    };

    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, badge: null },
        { id: 'user-management', label: 'User Management', icon: <PeopleIcon />, badge: null }
    ];

    return (
        <Box sx={{
            width: { xs: '100%', sm: 280 },
            height: '100vh',
            background: 'linear-gradient(180deg, #1a1f36 0%, #0f1419 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            position: { xs: 'relative', sm: 'fixed' },
            left: 0,
            top: 0,
            zIndex: 1000,
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.4)',
            borderRight: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
            {/* Header */}
            <Box sx={{
                p: 3,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(91, 33, 182, 0.1) 100%)'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Box sx={{
                        width: 52,
                        height: 52,
                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(124, 58, 237, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{
                            color: '#ffffff',
                            fontWeight: 800,
                            fontSize: '1.35rem',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em'
                        }}>
                            Admin Panel
                        </Typography>
                        <Typography sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            mt: 0.3
                        }}>
                            Management
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Quick Navigation - Main Dashboard */}
            <Box sx={{ px: 2.5, pt: 2, pb: 3 }}>
                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    mb: 1.5,
                    px: 1
                }}>
                    Quick Access
                </Typography>
                <Box
                    onClick={handleMainDashboard}
                    sx={{
                        p: 2,
                        borderRadius: '14px',
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: '2px solid rgba(16, 185, 129, 0.3)',
                        boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                        },
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 32px rgba(16, 185, 129, 0.35)',
                            border: '2px solid rgba(16, 185, 129, 0.5)',
                            '&::before': {
                                opacity: 1
                            }
                        },
                        '&:active': {
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                        <Box sx={{
                            width: 40,
                            height: 40,
                            background: 'rgba(255, 255, 255, 0.25)',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                            <HomeIcon sx={{ fontSize: 22, color: '#ffffff' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                lineHeight: 1.3,
                                mb: 0.3
                            }}>
                                Trading Dashboard
                            </Typography>
                            <Typography sx={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                letterSpacing: '0.02em'
                            }}>
                                Go to main view
                            </Typography>
                        </Box>
                        <Box sx={{
                            width: 28,
                            height: 28,
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                            <Typography sx={{ color: '#ffffff', fontSize: '1.2rem', fontWeight: 700 }}>→</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', mx: 2 }} />

            {/* Navigation */}
            <Box sx={{ py: 3, px: 2, flex: 1, overflowY: 'auto' }}>
                <Typography sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    mb: 1.5,
                    px: 1
                }}>
                    Admin Management
                </Typography>
                <List sx={{ p: 0 }}>
                    {menuItems.map((item) => (
                        <ListItem
                            key={item.id}
                            onClick={() => onTabChange(item.id)}
                            sx={{
                                mb: 1,
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                background: activeTab === item.id
                                    ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(91, 33, 182, 0.15) 100%)'
                                    : 'transparent',
                                border: activeTab === item.id
                                    ? '1px solid rgba(124, 58, 237, 0.4)'
                                    : '1px solid transparent',
                                boxShadow: activeTab === item.id
                                    ? '0 4px 16px rgba(124, 58, 237, 0.15)'
                                    : 'none',
                                '&:hover': {
                                    background: activeTab === item.id
                                        ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.25) 0%, rgba(91, 33, 182, 0.2) 100%)'
                                        : 'rgba(255, 255, 255, 0.05)',
                                    transform: 'translateX(4px)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 44,
                                color: activeTab === item.id ? '#a78bfa' : 'rgba(255, 255, 255, 0.6)'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: activeTab === item.id ? 700 : 600,
                                        fontSize: '0.95rem',
                                        color: activeTab === item.id ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'
                                    }
                                }}
                            />
                            {item.badge && (
                                <Badge
                                    badgeContent={item.badge}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                />
                            )}
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

            {/* User Profile */}
            <Box sx={{ p: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    mb: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)'
                }}>
                    <Avatar sx={{
                        width: 44,
                        height: 44,
                        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                        color: '#1a1f36',
                        fontWeight: 800,
                        fontSize: '1rem',
                        mr: 1.5
                    }}>
                        AP
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            lineHeight: 1.3
                        }}>
                            Admin Panel
                        </Typography>
                        <Typography sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.75rem',
                            fontWeight: 600
                        }}>
                            Super Admin
                        </Typography>
                    </Box>
                </Box>

                <ListItem
                    onClick={onLogout}
                    sx={{
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: 'rgba(239, 68, 68, 0.15)',
                            transform: 'translateX(4px)'
                        }
                    }}
                >
                    <ListItemIcon sx={{
                        minWidth: 44,
                        color: '#ef4444'
                    }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sign Out"
                        primaryTypographyProps={{
                            sx: {
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                color: '#ef4444'
                            }
                        }}
                    />
                </ListItem>
            </Box>
        </Box>
    );
};

export default AdminSidebar;